import { useCallback, useEffect, useState } from "react";
import type { SpeedUnit } from "./types";

const UNIT_CYCLE: SpeedUnit[] = ["kn", "ms", "kmh", "mph"];
const UNIT_KEY = "unit";

function readStoredUnit(): SpeedUnit {
  if (typeof localStorage === "undefined") return "kn";
  const raw = localStorage.getItem(UNIT_KEY);
  return UNIT_CYCLE.includes(raw as SpeedUnit) ? (raw as SpeedUnit) : "kn";
}

/** Persisted speed unit with a cycle helper. Mirrors the legacy unit toggle. */
export function useUnit() {
  const [unit, setUnitState] = useState<SpeedUnit>(readStoredUnit);

  const setUnit = useCallback((next: SpeedUnit) => {
    setUnitState(next);
    try {
      localStorage.setItem(UNIT_KEY, next);
    } catch {
      // localStorage may be disabled — silently degrade.
    }
  }, []);

  const cycleUnit = useCallback(() => {
    setUnitState((prev) => {
      const idx = UNIT_CYCLE.indexOf(prev);
      const next = UNIT_CYCLE[(idx + 1) % UNIT_CYCLE.length] ?? "kn";
      try {
        localStorage.setItem(UNIT_KEY, next);
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  return { unit, setUnit, cycleUnit };
}

function computeVisibleDayCount(width: number): number {
  if (width < 400) return 5;
  if (width < 600) return 6;
  return 7;
}

/**
 * Number of forecast days currently visible — drives both the day strip and
 * the per-spot forecast grid. Recomputes on resize but only triggers a
 * rerender when the bucketed count actually changes.
 */
export function useVisibleDayCount(): number {
  const [count, setCount] = useState(() =>
    typeof window === "undefined" ? 8 : computeVisibleDayCount(window.innerWidth),
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => {
      const next = computeVisibleDayCount(window.innerWidth);
      setCount((prev) => (prev === next ? prev : next));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return count;
}
